import React, { useState, useEffect, useMemo } from 'react';

const API_URL = import.meta.env.VITE_API_URL;

const Help = ({ showAlert }) => {
    const [helpData, setHelpData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedSections, setExpandedSections] = useState(new Set());

    // デフォルトのヘルプコンテンツ
    const defaultHelp = {
        version: 1,
        sections: [
            {
                id: 'dashboard',
                title: 'ダッシュボード',
                content: `## 概要
ダッシュボードでは、当期の売上・経費・所得の概要を一目で確認できます。

## 表示項目
- **売上（収入）**: 売上高などの収益科目の合計
- **経費**: 経費科目の合計
- **所得（差引利益）**: 売上 - 経費
- **青色申告特別控除**: 最大65万円

## 月別売上グラフ
1月〜12月の売上推移をグラフで表示します。

## 使い方
左メニューの「ダッシュボード」をクリックすると表示されます。年度セレクタで表示年度を切り替えられます。`
            },
            {
                id: 'journal-entry',
                title: '仕訳入力',
                content: `## 概要
日々の取引を仕訳として記録します。

## 新規登録
1. 左メニューの「仕訳入力」をクリック
2. 日付を入力（デフォルトは当日）
3. 摘要（取引の説明）を入力
4. 借方科目・金額を入力
5. 貸方科目・金額を入力
6. 「登録」ボタンをクリック

## 複合仕訳
「行を追加」ボタンで借方・貸方それぞれに複数行を追加できます。

## 消費税区分
各明細行に税率（10%、8%軽減、対象外など）を設定できます。

## 編集
1. 仕訳一覧の「編集」ボタンをクリック
2. 内容を修正
3. 「更新」ボタンをクリック

## 削除
1. 仕訳一覧の「削除」ボタンをクリック
2. 確認ダイアログで「OK」をクリック
※ 削除された仕訳は「削除済」として履歴に残ります（電子帳簿保存法対応）`
            },
            {
                id: 'accounts',
                title: '勘定科目マスタ',
                content: `## 概要
勘定科目の追加・編集・削除を行います。

## 科目の追加
1. 「新規追加」ボタンをクリック
2. コード、名称、読み、大区分、ホーム（借方/貸方）を入力
3. 「保存」ボタンをクリック

## 科目の編集
1. 科目一覧の「編集」ボタンをクリック
2. 内容を修正
3. 「保存」ボタンをクリック

## 科目の削除
1. 科目一覧の「削除」ボタンをクリック
2. 確認ダイアログで「OK」をクリック

## 勘定区分
- **資産**: 現金、預金、売掛金など
- **負債**: 買掛金、未払金、借入金など
- **純資産**: 元入金、事業主借、事業主貸など
- **収益**: 売上高、雑収入など
- **費用**: 仕入高、給料、地代家賃など`
            },
            {
                id: 'ledger',
                title: '総勘定元帳',
                content: `## 概要
勘定科目ごとの取引履歴と残高を確認できます。

## 使い方
1. 左メニューの「総勘定元帳」をクリック
2. 上部のセレクタで勘定科目を選択
3. 該当科目の取引一覧が表示されます

## 表示項目
- 日付
- 相手科目
- 摘要
- 借方金額
- 貸方金額
- 残高

## 変更履歴
「変更履歴」ボタンをクリックすると、仕訳の作成・修正・削除の履歴を確認できます（電子帳簿保存法対応）。`
            },
            {
                id: 'trial-balance',
                title: '試算表',
                content: `## 概要
すべての勘定科目の残高を一覧表示します。

## 表示項目
- 勘定科目
- 借方残高
- 貸方残高

## 使い方
左メニューの「試算表」をクリックすると表示されます。

## 注意
借方合計と貸方合計が一致していることを確認してください。`
            },
            {
                id: 'financial-statements',
                title: '決算書',
                content: `## 概要
損益計算書と貸借対照表を表示します。

## 損益計算書（P/L）
- 売上高（収益）
- 売上原価
- 経費
- 営業利益

## 貸借対照表（B/S）
- 資産の部
- 負債の部
- 純資産の部

## 印刷・PDF出力
「印刷」ボタンで印刷、「PDF出力」ボタンでPDFファイルとして保存できます。`
            },
            {
                id: 'data-management',
                title: 'データ管理',
                content: `## 概要
年度設定、組織情報、データの入出力などを管理します。

## タブ一覧
- **年度・保存**: 年度の管理、決算月の設定
- **組織情報**: 屋号・氏名の変更
- **取引先**: インボイス対応の取引先マスタ
- **勘定科目**: 勘定科目テンプレートの管理
- **CSV入出力**: 仕訳データのエクスポート・インポート
- **家事按分設定**: 経費の事業使用割合を事前設定
- **削除**: データの削除操作`
            },
            {
                id: 'credit-card',
                title: 'クレジットカード管理',
                content: `## 概要
クレジットカードの引き落とし管理と未払残高の追跡ができます。

## カードの登録（データ管理 → クレジットカード）
1. カード名、締め日、支払日、支払月オフセットを設定
2. 支払口座（引き落とし口座）と未払科目（未払金など）を選択
3. 開始残高・基準日を必要に応じて入力

## 仕訳入力でのカード選択
貸方科目にカードの未払科目（例：未払金）を選択すると、自動的にそのカードに紐付きます。複数カードがある場合はカード名のセレクタが表示されます。

## 開始残高とは
アプリを使い始める前にすでに発生していた、まだ引き落とされていない請求額です。

**例**: 2月からアプリ使用開始。1月に10,000円の買い物があり未精算 → 開始残高 = 10,000円

この金額はアプリの仕訳として入力不要です（入力するとダブルカウントになります）。ダッシュボードの残高に自動加算されます。

## 基準日とは
開始残高の「いつ時点の残高か」を記録するための日付です（記録用のメモ。計算には影響しません）。利用開始日か開始残高の締め日を入れておくと管理しやすいです。

## ダッシュボードの残高計算
**カード残高 = 新規購入額合計 − 精算額合計 + 開始残高**

## カード精算の記録
カード精算画面から、締め日ごとの引き落とし仕訳を一括生成できます。「未払金 / 普通預金」の仕訳が自動作成されます。`
            }
        ],
        updatedAt: new Date().toISOString()
    };

    // ヘルプデータを読み込み
    useEffect(() => {
        const loadHelp = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_URL}?action=getHelp`);
                if (response.ok) {
                    const data = await response.json();
                    setHelpData(data.help || defaultHelp);
                } else {
                    setHelpData(defaultHelp);
                }
            } catch (err) {
                console.error('Failed to load help:', err);
                setHelpData(defaultHelp);
            } finally {
                setIsLoading(false);
            }
        };
        loadHelp();
    }, []);

    // 検索でフィルタリング
    const filteredSections = useMemo(() => {
        if (!helpData?.sections) return [];
        if (!searchTerm.trim()) return helpData.sections;
        const term = searchTerm.toLowerCase();
        return helpData.sections.filter(s =>
            s.title.toLowerCase().includes(term) ||
            s.content.toLowerCase().includes(term)
        );
    }, [helpData, searchTerm]);

    // セクションの展開/折りたたみ
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId);
            } else {
                newSet.add(sectionId);
            }
            return newSet;
        });
    };

    // すべて展開/折りたたみ
    const expandAll = () => {
        if (expandedSections.size === filteredSections.length) {
            setExpandedSections(new Set());
        } else {
            setExpandedSections(new Set(filteredSections.map(s => s.id)));
        }
    };

    // Markdownをシンプルに変換
    const renderContent = (content) => {
        return content.split('\n').map((line, i) => {
            if (line.startsWith('## ')) {
                return <h4 key={i} style={{marginTop: '16px', marginBottom: '8px', color: '#2c3e50'}}>{line.slice(3)}</h4>;
            } else if (line.startsWith('### ')) {
                return <h5 key={i} style={{marginTop: '12px', marginBottom: '6px', color: '#34495e'}}>{line.slice(4)}</h5>;
            } else if (line.startsWith('- **')) {
                const match = line.match(/^- \*\*(.+?)\*\*: (.+)$/);
                if (match) {
                    return <p key={i} style={{margin: '4px 0 4px 16px'}}><strong>{match[1]}</strong>: {match[2]}</p>;
                }
                return <p key={i} style={{margin: '4px 0 4px 16px'}}>{line.slice(2)}</p>;
            } else if (line.startsWith('- ')) {
                return <p key={i} style={{margin: '4px 0 4px 16px'}}>{line.slice(2)}</p>;
            } else if (line.match(/^\d+\. /)) {
                return <p key={i} style={{margin: '4px 0 4px 16px'}}>{line}</p>;
            } else if (line.startsWith('※')) {
                return <p key={i} style={{margin: '8px 0', color: '#7f8c8d', fontSize: '13px'}}>{line}</p>;
            } else if (line.trim() === '') {
                return <br key={i} />;
            } else {
                return <p key={i} style={{margin: '4px 0'}}>{line}</p>;
            }
        });
    };

    if (isLoading) {
        return (
            <div className="page-container">
                <h2>ヘルプ</h2>
                <p style={{textAlign: 'center', padding: '40px'}}>読み込み中...</p>
            </div>
        );
    }

    return (
        <div className="page-container">
            <h2>ヘルプ</h2>

            {/* 検索バー */}
            <div style={{marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center'}}>
                <input
                    type="text"
                    className="form-control"
                    placeholder="検索..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{flex: 1, maxWidth: '400px'}}
                />
                <button
                    className="btn btn-secondary"
                    onClick={expandAll}
                >
                    {expandedSections.size === filteredSections.length ? 'すべて閉じる' : 'すべて開く'}
                </button>
                {searchTerm && (
                    <span style={{color: '#666', fontSize: '14px'}}>
                        {filteredSections.length}件
                    </span>
                )}
            </div>

            {/* セクション一覧 */}
            {filteredSections.length === 0 ? (
                <div className="card" style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                    該当するヘルプ項目がありません
                </div>
            ) : (
                filteredSections.map(section => (
                    <div key={section.id} className="card" style={{marginBottom: '12px'}}>
                        <div
                            onClick={() => toggleSection(section.id)}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px',
                                margin: '-20px',
                                marginBottom: expandedSections.has(section.id) ? '16px' : '-20px',
                                backgroundColor: expandedSections.has(section.id) ? '#f8f9fa' : 'transparent',
                                borderRadius: expandedSections.has(section.id) ? '8px 8px 0 0' : '8px'
                            }}
                        >
                            <h3 style={{margin: 0, fontSize: '16px', color: '#2c3e50'}}>
                                {section.title}
                            </h3>
                            <span style={{fontSize: '18px', color: '#666'}}>
                                {expandedSections.has(section.id) ? '▼' : '▶'}
                            </span>
                        </div>
                        {expandedSections.has(section.id) && (
                            <div style={{fontSize: '14px', lineHeight: '1.6'}}>
                                {renderContent(section.content)}
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
};

export default Help;
